import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm, Controller } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { Stack, Switch, styled } from "@mui/material";
import { Tab, Tabs, Form, Button } from "react-bootstrap";
import { PhoneInput } from "react-international-phone";
import {
  UserActions,
  authActions,
  instructorActions,
} from "../../../../../redux/actions";
import {
  roles,
  getNameById,
  countryConstants,
  translationConstants,
  fieldLengthConstants,
  Directions,
  preferedLanguagesContants,
  timezoneConstants,
  bloodTypes,
  sportConstantsReversed,
} from "../../../../../constants";
import { allSharedComponents } from "../../../../../shared";
import {
  antSwitchStyle,
  isPhoneValid,
  mergeDateTimeToServerFormat,
  serverFormatToDateObject,
  serverFormatToDateString,
  UTCWithTimezoneToISOString,
  appendMandatorySignAndTimezoneText,
} from "../../../../../system";
import moment from "moment-timezone";
import AdminCertificateView from "../../../../../shared/components/ApproveRejectCertificate/AdminCertificateView";

const { CustomModal, ReactDatePickerWrapper } = allSharedComponents;

const tabs = {
  general: "generalTab",
  documents: "documentsTab",
  qualifications: "qualificationsTab",
};

const ViewInstructorModal = ({
  isOpen,
  setIsOpen,
  gridState,
  updateInstructorStatus,
  setIsUpdate,
  setInstructorStatus,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const AntSwitch = styled(Switch)(({ theme }) => antSwitchStyle(theme));

  const instructorData = useSelector(
    (state) => state.InstructorReducer.instructorData
  );

  const cities = useSelector((state) => state.LoginReducer.cities);
  const countries = useSelector((state) => state.LoginReducer.countries);
  const direction = useSelector(
    (state) => state.LanguageChangeReducer.direction
  );

  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedCity, setSelectedCity] = useState("");
  const [userDocuments, setUserDocuments] = useState([]);
  const [activeTab, setActiveTab] = useState(tabs.general);
  const [selectedCountry, setSelectedCountry] = useState("");
  const [userCertificates, setuserCertificates] = useState([]);
  const [startDate, setStartDate] = useState(new Date("1/1/1990"));
  const [initialFormValues, setInitialFormValues] = useState({});
  const [isActivate, setIsActivate] = useState(false);

  const {
    setValue,
    register,
    handleSubmit,
    control,
    formState: { errors },
    trigger,
    reset,
  } = useForm();

  const toggleEditMode = () => {
    if (isEditMode) {
      reset(initialFormValues);
      const dob = instructorData?.userInfo?.dobString;
      if (dob) {
        setStartDate(serverFormatToDateObject(dob));
      }
      setSelectedCountry(instructorData?.userInfo?.countryId);
      setSelectedCity(instructorData?.userInfo?.cityId);
    }
    setIsEditMode(!isEditMode);
  };

  const onSubmit = (data) => {
    const userUpdateData = {
      englishFirstName: data.firstName,
      englishLastName: data.lastName,
      arabicFirstName: data.firstNameArabic,
      arabicLastName: data.lastNameArabic,
      email: data.email,
      phoneNumber: data.phoneNumber,
      gender: data.gender,
      dobString: mergeDateTimeToServerFormat(
        startDate,
        moment().set(timezoneConstants.twelveAM).toDate()
      ),
      countryId: Number(data.countryresidence),
      id: instructorData.userInfo.id,
      nationality: data.nationality,
      role: instructorData.userInfo.role,
      isActive: instructorData.userInfo.isActive,
      cityId:
        data.countryresidence == countryConstants.SAUDIA_ARABIA
          ? parseInt(selectedCity)
          : null,
      preferredLangauge: data.preferredLangauge,
      bloodGroup: data.bloodGroup,
    };

    toggleEditMode();
    dispatch(
      UserActions.updateUser(
        userUpdateData,
        instructorActions.getAllInstructors(gridState)
      )
    );
    dispatch(instructorActions.toggleViewModal(false));
  };

  useEffect(() => {
    // Set initial form values from userData
    setValue("firstName", instructorData?.userInfo?.englishFirstName);
    setValue("lastName", instructorData?.userInfo?.englishLastName);
    setValue("firstNameArabic", instructorData?.userInfo?.arabicFirstName);
    setValue("lastNameArabic", instructorData?.userInfo?.arabicLastName);
    setValue("email", instructorData?.userInfo?.email);
    setValue("phoneNumber", instructorData?.userInfo?.phoneNumber);
    setValue("gender", instructorData?.userInfo?.gender);
    setValue("nationality", instructorData?.userInfo?.nationality);
    setValue("countryresidence", instructorData?.userInfo?.countryId);
    setValue("cityresidence", instructorData?.userInfo?.cityId);
    setValue("preferredLangauge", instructorData?.userInfo?.preferredLangauge);
    setValue("bloodGroup", instructorData?.userInfo?.bloodGroup);

    if (instructorData?.userInfo?.dobString) {
      const dateObject = serverFormatToDateObject(
        instructorData?.userInfo?.dobString
      );
      setStartDate(dateObject);
      setValue("dateOfBirth", dateObject, { shouldValidate: true });
    }

    setSelectedCountry(instructorData?.userInfo?.countryId);
    setSelectedCity(instructorData?.userInfo?.cityId);

    setUserDocuments(instructorData?.documents);
    setuserCertificates(instructorData?.certificates);
    setIsActivate(instructorData?.userInfo?.instructorStatus === 1);
    // eslint-disable-next-line
  }, [dispatch, instructorData]);

  useEffect(() => {
    //Set initial form values when data is fetched
    setInitialFormValues({
      firstName: instructorData?.userInfo?.englishFirstName,
      lastName: instructorData?.userInfo?.englishLastName,
      firstNameArabic: instructorData?.userInfo?.arabicFirstName,
      lastNameArabic: instructorData?.userInfo?.arabicLastName,
      email: instructorData?.userInfo?.email,
      phoneNumber: instructorData?.userInfo?.phoneNumber,
      gender: instructorData?.userInfo?.gender,
      nationality: instructorData?.userInfo?.nationality,
      countryresidence: instructorData?.userInfo?.countryId,
      cityresidence: instructorData?.userInfo?.cityId,
      dateOfBirth: serverFormatToDateObject(
        instructorData?.userInfo?.dobString
      ),
      preferredLangauge: instructorData?.userInfo?.preferredLangauge,
      bloodGroup: instructorData?.userInfo?.bloodGroup,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instructorData]);

  useEffect(() => {
    //We need cities only if the country is Saudi Arabia, countryId:2 is assigned to it in database.
    if (
      selectedCountry == countryConstants.SAUDIA_ARABIA &&
      cities?.length <= 0
    ) {
      dispatch(authActions.fetchCities(selectedCountry));
    }
    // eslint-disable-next-line
  }, [selectedCountry]);

  useEffect(() => {
    setSelectedCity(instructorData?.userInfo?.cityId);
    setValue("cityresidence", instructorData?.userInfo?.cityId);
    // eslint-disable-next-line
  }, [cities]);

  useEffect(() => {
    if (!cities) {
    }
  }, [cities]);

  const getSportNames = (sportIds) => {
    return sportIds?.map((id) => (
      <div className="font-14 text-black">
        {t(translationConstants[sportConstantsReversed[id]])}
      </div>
    ));
  };

  return (
    <CustomModal
      size="lg"
      scrollable
      show={isOpen}
      setShow={() => {
        setIsEditMode(false);
        setIsOpen();
      }}
      customClass="view-user-modal"
    >
      <section className="modal-tab-container">
        <div className="ViewHeader">
          <p className="custom-modal-title">
            {isEditMode
              ? t(translationConstants.editInstructor)
              : t(translationConstants.viewInstructor)}
          </p>
          <p
            className="editButton"
            onClick={() => {
              setIsEditMode(true);
            }}
          >
            {!isEditMode && (
              <>
                {t(translationConstants.edit)}
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="29"
                  height="29"
                  viewBox="0 0 29 29"
                  fill="none"
                >
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M26.5833 24.167V15.7087H24.1667V24.167H4.83332V4.83366H13.2917V2.41699H4.83332C3.49863 2.41699 2.41666 3.49897 2.41666 4.83366V24.167C2.41666 25.5017 3.49863 26.5837 4.83332 26.5837H24.1667C25.5013 26.5837 26.5833 25.5017 26.5833 24.167ZM23.1735 3.29113C22.6197 2.73287 21.8613 2.41699 21.0701 2.41699C20.2799 2.41699 19.5223 2.73216 18.9653 3.29258L8.85284 13.4049C7.94921 14.2011 7.35685 15.3858 7.25391 16.66L7.24999 20.5448V21.7531H12.246C13.6166 21.6591 14.813 21.061 15.657 20.0874L25.7121 10.0365C26.27 9.47869 26.5834 8.72206 26.5834 7.93313C26.5834 7.1442 26.27 6.38757 25.7121 5.82971L23.1735 3.29113ZM12.1606 19.3395C12.8062 19.2938 13.407 18.9934 13.8895 18.4415L21.2143 11.1167L17.8862 7.78836L10.5078 15.1644C10.0182 15.5974 9.71536 16.2031 9.66666 16.7573V19.3372L12.1606 19.3395ZM19.5954 6.0798L22.9232 9.40782L24.0033 8.3277C24.1079 8.22305 24.1667 8.08112 24.1667 7.93313C24.1667 7.78514 24.1079 7.6432 24.0033 7.53856L21.4613 4.99661C21.3579 4.89232 21.217 4.83366 21.0701 4.83366C20.9232 4.83366 20.7824 4.89232 20.6789 4.99661L19.5954 6.0798Z"
                    fill="#007F58"
                  />
                </svg>
              </>
            )}
            {isEditMode && (
              <>
                {t(translationConstants.STATUS)}{" "}
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  className="mx-2"
                >
                  <AntSwitch
                    defaultChecked={isActivate}
                    onClick={() => {
                      setIsUpdate((prev) => !prev);
                      updateInstructorStatus(instructorData);
                      setInstructorStatus(
                        instructorData.userInfo.instructorStatus
                      );
                    }}
                    inputProps={{ "aria-label": "ant design" }}
                  />
                </Stack>
              </>
            )}
          </p>
        </div>
        <p className="horizentalLine" />
        <Tabs
          className={"m-0"}
          activeKey={activeTab}
          onSelect={(k) => setActiveTab(k)}
        >
          <Tab eventKey={tabs.general} title={t(translationConstants.general)}>
            <Form
              noValidate
              className="ViewUserContent ViewUserContentForm"
              onSubmit={handleSubmit(onSubmit)}
            >
              {/* firstLastName */}
              <div className="inputRow">
                <Form.Group className="form-group">
                  <Form.Label className="Form-labels">
                    {t(translationConstants.firstName)}:
                  </Form.Label>
                  <Form.Control
                    type="text"
                    id="firstName"
                    disabled={!isEditMode}
                    {...register("firstName", {
                      required: t(translationConstants.firstNameRequired),
                      pattern: {
                        value: /^[A-Za-z\s]+$/i,
                        message: t(translationConstants.invalidFirstName),
                      },
                    })}
                  />
                  {errors.firstName && (
                    <p className="errorText">{errors.firstName.message}</p>
                  )}
                </Form.Group>

                <Form.Group className="form-group">
                  <Form.Label className="Form-labels">
                    {t(translationConstants.lastName)}:
                  </Form.Label>
                  <Form.Control
                    {...register("lastName", {
                      required: t(translationConstants.lastNameRequired),
                      pattern: {
                        value: /^[A-Za-z\s]+$/i,
                        message: t(translationConstants.invalidLastName),
                      },
                    })}
                    id="lastName"
                    disabled={!isEditMode}
                    type="text"
                  />
                  {errors.lastName && (
                    <p className="errorText">{errors.lastName.message}</p>
                  )}
                </Form.Group>
              </div>

              {/* arabiclastName */}
              <div className="inputRow">
                <Form.Group className="form-group">
                  <Form.Label className="Form-labels">
                    {t(translationConstants.firstNameArabic)}:{" "}
                    <span>{t(translationConstants.optional)}</span>{" "}
                  </Form.Label>
                  <Form.Control
                    {...register("firstNameArabic", {})}
                    id="firstNameArabic"
                    type="text"
                    maxLength={fieldLengthConstants.TEXT_FIELD_LENGTH}
                    disabled={!isEditMode}
                    style={{ direction: Directions.rtl }}
                  />
                  {errors.firstNameArabic && (
                    <p className="errorText">
                      {errors.firstNameArabic.message}
                    </p>
                  )}
                </Form.Group>

                <Form.Group className="form-group">
                  <Form.Label className="Form-labels">
                    {t(translationConstants.lastNameArabic)}:{" "}
                    <span>{t(translationConstants.optional)}</span>
                  </Form.Label>
                  <Form.Control
                    {...register("lastNameArabic", {})}
                    id="lastNameArabic"
                    disabled={!isEditMode}
                    type="text"
                    maxLength={fieldLengthConstants.TEXT_FIELD_LENGTH}
                    style={{ direction: Directions.rtl }}
                  />
                  {errors.lastNameArabic && (
                    <p className="errorText">{errors.lastNameArabic.message}</p>
                  )}
                </Form.Group>
              </div>

              {/* emailPhoneNumber */}
              <div className="inputRow">
                <Form.Group className="form-group">
                  <Form.Label className="Form-labels">
                    {t(translationConstants.email)}
                  </Form.Label>
                  <Form.Control
                    {...register("email", {
                      required: t(translationConstants.emailRequired),
                      pattern: {
                        value:
                          /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{1,}$/i,
                        message: t(translationConstants.invalidEmail),
                      },
                    })}
                    id="email"
                    disabled={!isEditMode}
                    maxLength={fieldLengthConstants.EMAIL_FIELD_LENGTH}
                    type="email"
                  />
                  {errors.email && (
                    <p className="errorText">{errors.email.message}</p>
                  )}
                </Form.Group>

                <Form.Group className="form-phone-group-max-width">
                  <Form.Label className="Form-labels">
                    {t(translationConstants.phoneNumber)}:
                  </Form.Label>
                  <Controller
                    name="phoneNumber"
                    control={control}
                    rules={{
                      required: t(translationConstants.phoneNumberRequired),
                      validate: (value) => {
                        const isValid = isPhoneValid(value);
                        if (!isValid) {
                          return t(translationConstants.enterValidPhoneNumber);
                        }
                        return true;
                      },
                    }}
                    render={({ field: { onChange, value } }) => (
                      <PhoneInput
                        defaultCountry="sa"
                        value={value}
                        disabled={!isEditMode}
                        onChange={onChange}
                        maxLength={
                          fieldLengthConstants.PHONENUMBER_FIELD_LENGTH
                        }
                      />
                    )}
                  />
                  {errors.phoneNumber && (
                    <p className="errorText">{errors.phoneNumber.message}</p>
                  )}
                </Form.Group>
              </div>

              <div className="inputRow">
                <Form.Group className="form-group">
                  <Form.Label className="Form-labels">
                    {appendMandatorySignAndTimezoneText(
                      t(translationConstants.dateOfBirth),
                      t,
                      false
                    )}
                  </Form.Label>
                  <Controller
                    name="dateOfBirth"
                    control={control}
                    rules={{
                      required: t(translationConstants.dateOfBirthRequired),
                    }}
                    render={({ field }) => (
                      <div
                        className={`react-datepicker-wrapper-parent ${
                          !isEditMode ? "disabled-bg" : ""
                        }`}
                      >
                        <ReactDatePickerWrapper
                          disabled={!isEditMode}
                          selected={startDate}
                          onChange={(date) => {
                            field.onChange(date);
                            setStartDate(date);
                          }}
                          maxDate={new Date(UTCWithTimezoneToISOString())}
                        />
                      </div>
                    )}
                  />
                  {errors.dateOfBirth && (
                    <p className="errorText">{errors.dateOfBirth.message}</p>
                  )}
                </Form.Group>

                <Form.Group className="form-group">
                  <Form.Label className="Form-labels">
                    {t(translationConstants.gender)}:
                  </Form.Label>
                  <Form.Select
                    disabled={!isEditMode}
                    {...register("gender", {
                      required: t(translationConstants.genderSelectionRequired),
                    })}
                    aria-label="Default select example"
                  >
                    <option value="">
                      {t(translationConstants.selectGender)}
                    </option>
                    <option value={0}>{t(translationConstants.male)}</option>
                    <option value={1}>{t(translationConstants.female)}</option>
                  </Form.Select>
                  {errors.gender && (
                    <p className="errorText">{errors.gender.message}</p>
                  )}
                </Form.Group>
              </div>

              <div className="inputRow">
                <Form.Group className="form-group">
                  <Form.Label className="Form-labels">
                    {t(translationConstants.nationality)}:
                  </Form.Label>
                  <Form.Select
                    {...register("nationality", {
                      required: t(translationConstants.nationalityRequired),
                    })}
                    disabled={!isEditMode}
                    aria-label="Default select example"
                  >
                    <option value="">
                      {t(translationConstants.selectNationality)}
                    </option>
                    {countries?.data?.map((data) => (
                      <option key={data.id} value={data.nationality}>
                        {direction === Directions.rtl
                          ? data.arabicNationality
                          : data.nationality}
                      </option>
                    ))}
                  </Form.Select>
                  {errors.nationality && (
                    <p className="errorText">{errors.nationality.message}</p>
                  )}
                </Form.Group>
                <Form.Group className="form-group">
                  <Form.Label className="Form-labels">
                    {t(translationConstants.countryOfResidence)}:
                  </Form.Label>
                  <Form.Select
                    {...register("countryresidence", {
                      required: t(
                        translationConstants.countryResidenceRequired
                      ),
                    })}
                    onChange={(e) => {
                      const value = Number(e.target.value);
                      setValue("countryresidence", value, {
                        shouldValidate: true,
                      });
                      setSelectedCountry(value);
                      trigger("countryresidence"); // Trigger validation for the 'countryresidence' field
                      e.stopPropagation();
                    }}
                    value={selectedCountry}
                    disabled={!isEditMode}
                    aria-label="Default select example"
                  >
                    <option value="">
                      {t(translationConstants.selectresidence)}
                    </option>
                    {countries?.data?.map((residence) => (
                      <option key={residence.id} value={residence.id}>
                        {residence.name}
                      </option>
                    ))}
                  </Form.Select>

                  {errors.countryresidence && (
                    <p className="errorText">
                      {errors.countryresidence.message}
                    </p>
                  )}
                </Form.Group>

                {selectedCountry === 2 && (
                  <Form.Group className="form-group">
                    <Form.Label
                      htmlFor="cityResidenceSelect"
                      className="Form-labels"
                    >
                      {t(translationConstants.cityofresidence)}:
                    </Form.Label>
                    <Form.Select
                      id="cityResidenceSelect"
                      {...register("cityresidence", {
                        required: t(translationConstants.cityResidenceRequired),
                      })}
                      onChange={(e) => {
                        const value = e.target.value;
                        setValue("cityresidence", value, {
                          shouldValidate: true,
                        });
                        setSelectedCity(value);
                        trigger("cityresidence"); // Trigger validation for the 'countryresidence' field
                        e.stopPropagation();
                      }}
                      disabled={!isEditMode}
                      aria-label="Default select example"
                    >
                      <option value="">
                        {t(translationConstants.selectcity)}
                      </option>
                      {cities?.map((city) => (
                        <option key={city.id} value={city.id}>
                          {city.name}
                        </option>
                      ))}
                    </Form.Select>
                    {errors.cityresidence && (
                      <p className="errorText">
                        {errors.cityresidence.message}
                      </p>
                    )}
                  </Form.Group>
                )}
                <Form.Group className="form-group-register">
                  <Form.Label
                    htmlFor="preferredLanguageSelect"
                    className="Form-labels"
                  >
                    {t(translationConstants.PREFERRED_LANGUAGE)}:
                  </Form.Label>
                  <Form.Select
                    id="preferredLanguageSelect"
                    {...register("preferredLangauge", {
                      required: t(
                        translationConstants.SELECT_PREFERRED_LANGUAGE_Required
                      ),
                    })}
                    disabled={!isEditMode}
                    onChange={(e) => {
                      const value = e.target.value;
                      setValue("preferredLangauge", value, {
                        shouldValidate: true,
                      });
                      trigger("preferredLangauge"); // Trigger validation for the 'countryresidence' field
                      e.stopPropagation();
                    }}
                  >
                    <option value="">
                      {t(translationConstants.SELECT_PREFERRED_LANGUAGE)}
                    </option>
                    <option value="0">
                      {preferedLanguagesContants.Arabic}
                    </option>
                    <option value="1">
                      {preferedLanguagesContants.English}
                    </option>
                  </Form.Select>
                  {errors.preferredLangauge && (
                    <p className="errorText">
                      {errors.preferredLangauge.message}
                    </p>
                  )}
                </Form.Group>
                <Form.Group className="form-group-register">
                  <Form.Label className="Form-labels">
                    {t(translationConstants.BLOOD_GROUP)}:
                  </Form.Label>
                  <Form.Select
                    className="custom-select-icon"
                    {...register("bloodGroup", {
                      required: t(translationConstants.BLOOD_GROUP_REQUIRED),
                    })}
                    disabled={!isEditMode}
                    onChange={(e) => {
                      const value = e.target.value;
                      setValue("bloodGroup", value, {
                        shouldValidate: true,
                      });
                      trigger("bloodGroup"); // Trigger validation for the 'countryresidence' field
                      e.stopPropagation();
                    }}
                  >
                    <option value="">
                      {t(translationConstants.SELECT_BLOOD_GROUP)}
                    </option>
                    {Object.values(bloodTypes).map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </Form.Select>
                  {errors.bloodGroup && (
                    <p className="errorText">{errors.bloodGroup.message}</p>
                  )}
                </Form.Group>
              </div>

              {isEditMode && (
                <div className="inputRow">
                  <div className="col-12 d-flex justify-content-lg-end justify-content-center">
                    <Button
                      className="update-button max-width-btn mt-2"
                      type="submit"
                    >
                      {t(translationConstants.Update)}
                    </Button>
                  </div>

                  <div className="col-12 d-flex justify-content-lg-start justify-content-center">
                    <button
                      onClick={toggleEditMode}
                      className="CancelUserView-button mt-2"
                      type="button"
                    >
                      {t(translationConstants.cancel)}
                    </button>
                  </div>
                </div>
              )}
            </Form>
          </Tab>

          {userDocuments?.length > 0 && (
            <Tab eventKey="documents" title={t(translationConstants.documents)}>
              <div className="inputRow">
                <Form.Group className="form-group-register">
                  <Form.Label className="Form-labels d-flex justify-content-between">
                    {t(translationConstants.verifiedDoc)}:
                  </Form.Label>
                  {userDocuments &&
                    userDocuments.map((document) => {
                      return document.files.map((file) => {
                        return (
                          <p className="fileNameDisplay expiryDateDisplay  mt-2">
                            {t(getNameById(document.documentTypeId))}
                            <div
                              key={file.documentId}
                              className="iconsDivUserDocument"
                            >
                              <svg
                                onClick={() => window.open(file.link)}
                                xmlns="http://www.w3.org/2000/svg"
                                width="20"
                                height="20"
                                viewBox="0 0 24 27"
                                fill="none"
                              >
                                <path
                                  d="M22 12.2609C22 12.2609 19 18.2609 12 18.2609C5 18.2609 2 12.2609 2 12.2609C2 12.2609 5 6.26086 12 6.26086C19 6.26086 22 12.2609 22 12.2609Z"
                                  stroke="#48722D"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                />
                                <circle
                                  cx="12"
                                  cy="12.2609"
                                  r="3"
                                  stroke="#48722D"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                />
                              </svg>
                            </div>
                          </p>
                        );
                      });
                    })}
                </Form.Group>

                <Form.Group className="form-group">
                  <Form.Label className="Form-labels">
                    {t(translationConstants.documentNo)}:
                  </Form.Label>
                  {userDocuments && (
                    <div
                      key={userDocuments[0]?.files[0].documentId}
                      className="expiryDateDisplay"
                    >
                      <p className="text-ellipsis pe-2">
                        {userDocuments[0]?.files[0]?.documentNumber || ""}
                      </p>
                    </div>
                  )}
                </Form.Group>
              </div>
              <div className="inputRow">
                <Form.Group className="form-group-register">
                  <Form.Label className="Form-labels">
                    {appendMandatorySignAndTimezoneText(
                      t(translationConstants.expiryData),
                      t,
                      false
                    )}
                  </Form.Label>

                  {
                    /*
                    We'll always show only one Expiry date even if there are mulitple files uploaded.
                    Because the uploaded documents are supposed to be different parts of same document.
                    Like front and back of an Id Card.
                  */

                    userDocuments && (
                      <div
                        key={userDocuments[0]?.files[0].documentId}
                        className="expiryDateDisplay"
                      >
                        <p>
                          {userDocuments[0]?.files[0]?.expiredOnString
                            ? serverFormatToDateString(
                                userDocuments[0]?.files[0]?.expiredOnString,
                                timezoneConstants.dayMonthYearFormat
                              )
                            : t(translationConstants.NO_EXPIRY_DATE)}
                        </p>
                      </div>
                    )
                  }
                  {errors.expiryData && (
                    <p className="errorText">{errors.expiryData.message}</p>
                  )}
                </Form.Group>
              </div>
            </Tab>
          )}

          {instructorData?.userInfo?.role !== roles.GeneralUser &&
            instructorData?.userInfo?.role !== roles.VerifiedUser && (
              <Tab
                eventKey="certificates"
                title={t(translationConstants.QUALIFICATIONS)}
              >
                <AdminCertificateView
                  isInstructorView={true}
                  isViewCertificatesOnly={true}
                  certificates={userCertificates}
                />
              </Tab>
            )}

          {instructorData?.freelanceInfo && (
            <Tab
              eventKey="freelanceDetail"
              title={t(translationConstants.FREELANCE_INFORMATION)}
            >
              <div className="px-2 pt-3">
                <div className="">
                  <h6 className="freelance-info-text w-100 d-flex justify-content-between align-items-center">
                    {t(translationConstants.TEACHING_LOCATION)}
                  </h6>
                </div>
                <div className="font-14 text-black">
                  {direction === Directions.rtl &&
                  instructorData?.freelanceInfo?.spotArabicName
                    ? instructorData?.freelanceInfo?.spotArabicName
                    : instructorData?.freelanceInfo?.spotName}
                </div>
                <div className="pt-3">
                  <h6 className="freelance-info-text w-100 d-flex justify-content-between align-items-center">
                    {t(translationConstants.SPORTS_OFFERED_WITH_BRACKETS)}
                  </h6>
                </div>
                <div>
                  {getSportNames(
                    instructorData?.freelanceInfo?.freelanceSports
                  )}
                </div>
              </div>
            </Tab>
          )}
        </Tabs>
      </section>
    </CustomModal>
  );
};

export default ViewInstructorModal;
